import { translate } from '@avensia/excite';
import { styled } from '@glitz/react';
import { produce } from 'immer';
import * as React from 'react';
import { ArrowRight, SearchIcon } from 'webapp/components/icons';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { FacetValues } from './FacetValueList';
import { Heading } from './general-styling';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { FieldSize } from 'webapp/components/field-size';
import { NnTestIdProps } from 'webapp/shared/test-ids';

type PropType = {
  facet: Excite.Esales.FacetApiModel;
  onFacetValueChange: (
    updatedFacet: Excite.Esales.FacetApiModel,
    changedValue: Excite.Esales.FacetValueApiModel,
  ) => void;
  closeFlyout?: () => void;
  compact?: boolean;
  showMoreFacets?: (facet: Excite.Esales.FacetApiModel) => void;
};

export interface FacetValueWithChildren extends Excite.Esales.FacetValueApiModel {
  children?: Excite.Esales.FacetValueApiModel[];
}

function createFacetHierarchy(facetValues: Excite.Esales.FacetValueApiModel[]): FacetValueWithChildren[] {
  const hierarchy: Record<string, FacetValueWithChildren> = {};

  facetValues.forEach(value => {
    const parts = value.text.split('--'); // Split the text by the separator
    const parentText = parts[0];
    const childText = parts[1];

    if (!hierarchy[parentText]) {
      // Create the parent if it doesn't exist
      hierarchy[parentText] = { ...value, text: parentText, children: [], count: 0 };
    }

    if (childText) {
      // Create the child and add it to the parent's children
      hierarchy[parentText].children = hierarchy[parentText].children || [];
      hierarchy[parentText].children.push(value);

      // Add the child's count to the parent's count
      hierarchy[parentText].count += value.count;
    } else {
      // If it's a standalone parent, ensure its count is preserved
      hierarchy[parentText].count += value.count;
    }
  });

  // Return the hierarchy as an array
  return Object.values(hierarchy);
}

export default React.memo(
  styled(function Facet(props: PropType) {
    const [searchText, setSearchText] = React.useState('');
    const hierarchy = createFacetHierarchy(props.facet.values);

    const filterFacetValues = hierarchy.filter(v => v.text.toLowerCase().includes(searchText.toLowerCase()));
    const compactFacetValues = hierarchy
      .filter(v => v.text.toLowerCase().includes(searchText.toLowerCase()))
      .splice(0, 10);

    const onClickFaceValue = React.useCallback(
      (value: Excite.Esales.FacetValueApiModel | Excite.Esales.FacetValueApiModel[], checked: boolean) => {
        if (Array.isArray(value)) {
          const newFacet = produce(props.facet, facet => {
            value.forEach(v => {
              const val = facet.values.find(f => f.text === v.text);
              if (!val) throw new Error('Expected to find value');
              val.selected = checked;
            });
          });
          props.onFacetValueChange(newFacet, newFacet.values[0]);
        } else {
          const newFacet = produce(props.facet, facet => {
            const val = facet.values.find(v => v.text === value.text);
            if (!val) throw new Error('Expected to find value');
            val.selected = checked;
          });
          props.onFacetValueChange(newFacet, newFacet.values.find(v => v.text === value.text)!);
        }
      },
      [props],
    );

    return (
      <>
        <SearchInputContainer css={props.compact && borderDecorator}>
          <SearchInput
            placeholder={translate(t => t.productListing.searchFilterValuePlaceholderText, {
              ['current_filter']: props.facet.text,
            })}
            value={searchText}
            onChange={e => setSearchText(e.currentTarget.value)}
          />
          <SearchIcon />
        </SearchInputContainer>
        <FacetValues
          facetList={{ ...props.facet, values: props.compact ? compactFacetValues : filterFacetValues }}
          onClickFaceValue={onClickFaceValue}
          compact={props.compact}
        />
        {props.compact && filterFacetValues.length > compactFacetValues.length && (
          <ShowMore
            as={Theme.Transparent}
            size={FieldSize.Small}
            disableRippleEffect
            onClick={() => props.showMoreFacets?.(props.facet)}
          >
            {translate(t => t.productListing.showAllFilters, {
              amount: `${filterFacetValues.length - compactFacetValues.length}`,
            })}
          </ShowMore>
        )}
      </>
    );
  }),
);

type FacetFlyoutPropType = {
  facet: Excite.Esales.FacetApiModel;
  onClick: () => void;
};

export function FacetFlyoutHeader(props: FacetFlyoutPropType) {
  const selectedValues = props.facet.values.filter(v => v.selected).map(facetValue => facetValue.displayText);

  return (
    <Base {...NnTestIdProps.FacetHeader}>
      <Header as={Theme.Transparent} onClick={props.onClick}>
        <Heading>{props.facet.text}</Heading>
        <HeadingText css={selectedValues.length > 0 && selectedValuesDecorator}>
          {selectedValues.length > 0 ? selectedValues.join(', ') : props.facet.values.length}
        </HeadingText>
        <ArrowRight />
      </Header>
    </Base>
  );
}

const SearchInputContainer = styled.div(
  createMaxSmallViewportDecorator({
    top: 0,
    position: 'sticky',
  }),
  {
    padding: { x: t => t.sharedSpacing.l, y: t => t.sharedSpacing.m },
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: t => t.facets?.search?.color,
    border: { y: { color: t => t.facets?.search?.borderColor, style: 'solid', width: 'thin' } },
    zIndex: 1,
  },
);

const borderDecorator = styled({
  borderTop: { color: t => t.facets?.search?.borderColor },
});

const SearchInput = styled.input({
  width: '100%',
  backgroundColor: 'inherit',
  border: { xy: { style: 'none' } },
  ':focus': {
    outline: { style: 'none' },
  },
});

const selectedValuesDecorator = styled(overflowEllipsisDecorator, {
  color: t => t.facets?.range?.sliderMarkColor,
  fontWeight: 'bold',
  maxWidth: 120,
});

const Base = styled.li({
  width: '100%',
  borderBottom: { style: 'solid', width: 'thin', color: t => t.facets?.borderColor },
  ':first-child': {
    borderTop: { style: 'solid', width: 'thin', color: t => t.facets?.borderColor },
  },
});

const HeadingText = styled.span({
  fontSize: t => t.fontSize.xxs,
  marginRight: t => t.sharedSpacing.xs,
  color: t => t.dark.color,
});

const Header = styled(Button, {
  padding: { left: t => t.sharedSpacing.l, right: t => t.sharedSpacing.m, y: t => t.sharedSpacing.m },
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
});

const ShowMore = styled(Button, {
  fontSize: t => t.facets?.facet?.fontSize,
  margin: { bottom: t => t.sharedSpacing.xxs },
});
